'use client';

import { useRouter } from 'next/navigation';
import { useMemo, ReactNode, useContext, useCallback, createContext } from 'react';
import { COOKIE_ACCESS_TOKEN, COOKIE_REFRESH_TOKEN } from '@/modules/product/utils';
import { User } from '@/modules/account/api';
import { removeCookie } from 'utils';

import { routes } from '../data/routes';
import { useCurrentLocale } from '../utils/client';

export const AuthenticationContext = createContext<{
	user: User | null;
	logout: () => void;
}>({
	user: null,
	logout: () => {},
});

export function AuthenticationContextProvider({
	children,
	user,
}: {
	children: ReactNode;
	user: User | null;
}) {
	const router = useRouter();
	const locale = useCurrentLocale();

	const logout = useCallback(() => {
		removeCookie(COOKIE_ACCESS_TOKEN);
		removeCookie(COOKIE_REFRESH_TOKEN);

		router.push(routes[locale].HOME);
		router.refresh();
	}, [router]);

	return (
		<AuthenticationContext.Provider
			value={useMemo(
				() => ({
					user,
					logout,
				}),
				[user, logout]
			)}
		>
			{children}
		</AuthenticationContext.Provider>
	);
}

export function useAuthentication() {
	const context = useContext(AuthenticationContext);

	if (!context) throw new Error('useAuthContext context must be use inside AuthProvider');

	return context;
}
